import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiHome, FiBarChart2, FiInfo, FiSearch, FiMoon, FiSun, FiCloud, FiLogOut, FiCheck, FiRefreshCw, FiAlertCircle, FiBriefcase, FiTarget, FiRepeat, FiClock } from "react-icons/fi";
import { slugify } from "../../utils/slug";
import { topicEmoji } from "../../utils/topicMeta";
import { dueCount } from "../../utils/revision";

const SYNC = {
  idle: { label: "Not synced", Icon: FiCloud },
  syncing: { label: "Saving…", Icon: FiRefreshCw },
  synced: { label: "Synced", Icon: FiCheck },
  error: { label: "Sync error", Icon: FiAlertCircle },
};

export default function Sidebar({
  questionData,
  dark,
  setDark,
  onNavigate,
  isAuthed,
  email,
  syncState,
  onSignIn,
  onSignOut,
}) {
  const location = useLocation();
  const [query, setQuery] = useState("");

  const path = location.pathname;
  const isHome = path === "/";
  const isDash = path.startsWith("/dashboard");
  const isPractice = path.startsWith("/practice");
  const isRevise = path.startsWith("/revise");
  const isMock = path.startsWith("/mock");
  const isApply = path.startsWith("/apply");
  const isAbout = path.startsWith("/about");

  const due = useMemo(() => dueCount(questionData), [questionData]);

  const { topics, solved, total } = useMemo(() => {
    let s = 0, t = 0;
    const list = (questionData || []).map((topic) => {
      const done = topic.doneQuestions || 0;
      const tot = topic.questions ? topic.questions.length : 0;
      s += done; t += tot;
      return {
        name: topic.topicName,
        slug: slugify(topic.topicName),
        done,
        total: tot,
        pct: tot ? Math.round((done / tot) * 100) : 0,
        custom: !!topic.Custom,
      };
    });
    return { topics: list, solved: s, total: t };
  }, [questionData]);

  const q = query.trim().toLowerCase();
  const shown = q ? topics.filter((t) => t.name.toLowerCase().includes(q)) : topics;
  const overall = total ? Math.round((solved / total) * 100) : 0;

  const go = () => onNavigate && onNavigate();

  return (
    <aside className="sidebar">
      <div className="sidebar-head">
        <Link to="/" className="brand" onClick={go}>
          <div className="brand-logo">A</div>
          <div className="brand-text">
            <span className="brand-name">Algonest</span>
            <span className="brand-sub">Your DSA workspace</span>
          </div>
        </Link>
      </div>

      <div className="sidebar-find">
        <FiSearch size={15} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a topic…"
          aria-label="Find a topic"
        />
      </div>

      <nav className="nav-scroll">
        <Link to="/" className={`nav-item ${isHome ? "active" : ""}`} onClick={go}>
          <FiHome size={16} /> Home
        </Link>
        <Link to="/dashboard" className={`nav-item ${isDash ? "active" : ""}`} onClick={go}>
          <FiBarChart2 size={16} /> Dashboard
        </Link>
        <Link to="/practice" className={`nav-item ${isPractice ? "active" : ""}`} onClick={go}>
          <FiTarget size={16} /> Practice
        </Link>
        <Link to="/revise" className={`nav-item ${isRevise ? "active" : ""}`} onClick={go}>
          <FiRepeat size={16} /> Revise
          {due > 0 ? <span className="nav-badge">{due}</span> : null}
        </Link>
        <Link to="/mock" className={`nav-item ${isMock ? "active" : ""}`} onClick={go}>
          <FiClock size={16} /> Mock
        </Link>
        <Link to="/apply" className={`nav-item ${isApply ? "active" : ""}`} onClick={go}>
          <FiBriefcase size={16} /> Apply
        </Link>
        <Link to="/about" className={`nav-item ${isAbout ? "active" : ""}`} onClick={go}>
          <FiInfo size={16} /> About
        </Link>

        <div className="nav-section">
          <span>Topics</span>
          <span className="count">{topics.length}</span>
        </div>

        {shown.length === 0 ? (
          <div className="topic-empty">No topics match “{query}”.</div>
        ) : (
          shown.map((t) => {
            const active = path === `/topic/${t.slug}`;
            return (
              <Link
                key={t.slug}
                to={`/topic/${t.slug}`}
                className={`topic-item ${active ? "active" : ""}`}
                onClick={go}
                title={`${t.name} — ${t.done}/${t.total} solved`}
              >
                <span className="topic-emoji">{topicEmoji(t.name)}</span>
                <span className="topic-name">{t.name}</span>
                {t.pct === 100 && t.total > 0 ? (
                  <span className="topic-dot" title="Completed" />
                ) : (
                  <span className="topic-pct">{t.pct}%</span>
                )}
              </Link>
            );
          })
        )}
      </nav>

      <div className="sidebar-foot">
        {isAuthed ? (
          <div className="acct">
            <div className="acct-avatar">{(email || "?").charAt(0).toUpperCase()}</div>
            <div className="acct-meta">
              <span className="acct-email" title={email}>
                {email}
              </span>
              <span className={`acct-sync ${syncState || "idle"}`}>
                {(() => {
                  const S = SYNC[syncState] || SYNC.idle;
                  const Icon = S.Icon;
                  return (
                    <>
                      <Icon size={12} className={syncState === "syncing" ? "spin" : ""} />
                      {S.label}
                    </>
                  );
                })()}
              </span>
            </div>
            <button type="button" className="acct-out" title="Sign out" onClick={onSignOut}>
              <FiLogOut size={15} />
            </button>
          </div>
        ) : (
          <button type="button" className="acct-signin" onClick={onSignIn}>
            <FiCloud size={15} /> Sign in to sync
          </button>
        )}
        <div className="sidebar-summary">
          <div className="ss-row">
            <span>Overall progress</span>
            <span>{solved}/{total}</span>
          </div>
          <div className="ss-bar">
            <div className="ss-fill" style={{ width: `${overall}%` }} />
          </div>
        </div>
        <button
          type="button"
          className="theme-toggle"
          onClick={() => {
            const next = !dark;
            setDark(next);
            window.localStorage["isDark"] = next;
          }}
        >
          {dark ? <FiSun size={16} /> : <FiMoon size={16} />}
          {dark ? "Light mode" : "Dark mode"}
        </button>
      </div>
    </aside>
  );
}
